import { useCallback } from 'react';
import { FormFieldState } from '../../../../core/components';
import { formatCpf, formatPhone } from '../../utils/masks';
import { PersonalDataFormValues, personalDataFormSchema } from '../../types/personalDataForm';
import { useTouchedFormState } from '../useTouchedFormState/useTouchedFormState';

export type PersonalDataFieldName = keyof PersonalDataFormValues;
export type PersonalDataFieldState = FormFieldState;

const INITIAL_VALUES: PersonalDataFormValues = { name: '', cpf: '', email: '', phone: '' };

const FIELD_FORMATTERS: Partial<Record<PersonalDataFieldName, (value: string) => string>> = {
  cpf: formatCpf,
  phone: formatPhone,
};

type UsePersonalDataFormResult = {
  values: PersonalDataFormValues;
  isValid: boolean;
  handleChange: (field: PersonalDataFieldName, rawValue: string) => void;
  handleBlur: (field: PersonalDataFieldName) => void;
  getFieldState: (field: PersonalDataFieldName) => PersonalDataFieldState;
  getFieldError: (field: PersonalDataFieldName) => string | undefined;
};

export function usePersonalDataForm(): UsePersonalDataFormResult {
  const { values, setValues, isValid, handleBlur, getFieldState, getFieldError } =
    useTouchedFormState<PersonalDataFormValues>({
      schema: personalDataFormSchema,
      initialValues: INITIAL_VALUES,
    });

  const handleChange = useCallback(
    (field: PersonalDataFieldName, rawValue: string) => {
      const formatter = FIELD_FORMATTERS[field];
      setValues((previous) => ({
        ...previous,
        [field]: formatter ? formatter(rawValue) : rawValue,
      }));
    },
    [setValues],
  );

  return { values, isValid, handleChange, handleBlur, getFieldState, getFieldError };
}
